import type { ConsultationConsultType } from '@gh/common/models/consultation/consultation'

/** Used only when `VITE_BRIDGE_API_BASE_URL` is unset (e.g. local defaults). */
const FALLBACK_BRIDGE_API_ORIGIN = 'https://app.usebridge.xyz'

/**
 * Browser Bridge origins (see `.env-cmdrc`):
 *
 * - `VITE_BRIDGE_API_BASE_URL` — Search + Soft (`pk_`). Often the tenant host, e.g.
 *   `https://greatly-health.app.usebridge.xyz`, which must match the key’s tenant.
 */

export type BridgePublishableConfig = {
  apiKey: string
  baseUrl: string
}

/** Search / soft eligibility — uses `VITE_BRIDGE_API_BASE_URL`. */
export function readBridgePublishableConfig(): BridgePublishableConfig | null {
  const apiKey = import.meta.env.VITE_BRIDGE_PUBLISHABLE_KEY?.trim()
  if (!apiKey) {
    return null
  }

  const rawBase = import.meta.env.VITE_BRIDGE_API_BASE_URL?.trim()
  const baseUrl = (rawBase || FALLBACK_BRIDGE_API_ORIGIN).replace(/\/$/, '')

  return { apiKey, baseUrl }
}

/** Bridge consult service type for provider-eligibility (soft) checks — sandbox-specific. */
export function readBridgeServiceTypeId(): string | null {
  const id = import.meta.env.VITE_BRIDGE_SERVICE_TYPE_ID?.trim()
  return id || null
}

/** Consult-type-aware service type for slot-page insured PE (main vs followup). */
export function readBridgeServiceTypeIdForConsultType(
  consultType: ConsultationConsultType,
): string | null {
  if (consultType === 'clinical_followup') {
    const id = import.meta.env.VITE_BRIDGE_SERVICE_TYPE_ID_FOLLOWUP?.trim()
    return id || null
  }
  return readBridgeServiceTypeId()
}

export const DEFAULT_BRIDGE_CONSENT_TO_TREAT_URL =
  'https://files.usebridge.com/CCM%2C%20RPM%2C%20CHI%2C%20PIN%2C%20SMA%20Consent%207.28.26.pdf'
export const DEFAULT_BRIDGE_PRIVACY_NOTICE_URL =
  'https://brdg.b-cdn.net/Bridge_Privacy_Policy_2024-04-14.pdf'
export const DEFAULT_BRIDGE_ASSIGNMENT_OF_BENEFITS_URL =
  'https://brdg.b-cdn.net/Assignment%20of%20Benefits%20(MA)%20-%202025-06-05.pdf'

function readBridgeConsentUrl(envValue: string | undefined, fallback: string): string {
  const trimmed = envValue?.trim()
  return trimmed || fallback
}

export function readBridgeConsentToTreatUrl(): string {
  return readBridgeConsentUrl(
    import.meta.env.VITE_BRIDGE_CONSENT_TO_TREAT_URL,
    DEFAULT_BRIDGE_CONSENT_TO_TREAT_URL,
  )
}

export function readBridgePrivacyNoticeUrl(): string {
  return readBridgeConsentUrl(
    import.meta.env.VITE_BRIDGE_PRIVACY_NOTICE_URL,
    DEFAULT_BRIDGE_PRIVACY_NOTICE_URL,
  )
}

export function readBridgeAssignmentOfBenefitsUrl(): string {
  return readBridgeConsentUrl(
    import.meta.env.VITE_BRIDGE_ASSIGNMENT_OF_BENEFITS_URL,
    DEFAULT_BRIDGE_ASSIGNMENT_OF_BENEFITS_URL,
  )
}

export function requireBridgePublishableConfig(): BridgePublishableConfig {
  const config = readBridgePublishableConfig()
  if (!config) {
    throw new Error('Bridge publishable key is not configured (VITE_BRIDGE_PUBLISHABLE_KEY)')
  }
  return config
}
